import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, Building2, KeyRound, ChevronDown, Dumbbell, ClipboardList, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../App';
import HamburgerMenu from '../components/HamburgerMenu';
import CoachDashboard from './coach/CoachDashboard';

const ORG_TYPES = [
  { value: 'school', label: 'School' },
  { value: 'gym', label: 'Gym' },
  { value: 'personal_trainer', label: 'Personal Trainer' },
  { value: 'sports_team', label: 'Sports Team' },
  { value: 'other', label: 'Other' },
];

const PRICING = [
  { tier: 'coach', name: 'Individual Coach', price: '$39', per: '/month', desc: 'For independent trainers', features: ['Up to 15 athletes', 'Athlete metrics dashboard', 'AI workout plan builder', 'CSV reports'] },
  { tier: 'team', name: 'Small Team / Gym', price: '$99', per: '/month', desc: 'For gyms & small clubs', features: ['Up to 75 athletes', 'Multiple teams & coaches', 'Nutritionist role & goal setting', 'Everything in Coach'], popular: true },
  { tier: 'school', name: 'School / Organization', price: '$199', per: '/month', desc: 'For schools & large programs', features: ['Unlimited athletes', 'Unlimited teams & staff', 'Org-wide compliance reporting', 'Everything in Team'] },
  { tier: 'enterprise', name: 'Enterprise', price: 'Custom', per: '', desc: 'Districts & franchises', features: ['Custom athlete limits', 'Priority support', 'Onboarding assistance', 'Contact us'] },
];

const STAFF_ROLES = [
  'org_admin',
  'coach',
  'assistant_coach',
  'nutritionist',
  'healthcare_professional',
];

const TeamsHub = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [joinCode, setJoinCode] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createTier, setCreateTier] = useState('coach');
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('gym');
  const [creating, setCreating] = useState(false);
  const [billingConfig, setBillingConfig] = useState(null);
  const [billingConfigReady, setBillingConfigReady] = useState(false);
  const commercialSignup = billingConfig?.organization_signup_mode === 'paid';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orgs/mine`);
      setMemberships(res.data.memberships || []);
    } catch (e) {
      toast.error('Failed to load your organizations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/subscriptions/config`)
      .then((r) => setBillingConfig(r.data))
      .catch(() => setBillingConfig(null))
      .finally(() => setBillingConfigReady(true));
  }, []);

  useEffect(() => {
    localStorage.removeItem('post_auth_redirect');

    const pending = localStorage.getItem(
      'pending_invite_code'
    );

    if (pending) {
      navigate(`/join/${pending}`);
      return;
    }

    load();
  }, [load, navigate]);

  const handleJoin = (e) => {
    e.preventDefault();

    const normalized = (
      joinCode.trim().toUpperCase()
    );

    if (!normalized) {
      return;
    }

    navigate(
      `/join/${normalized}`
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) { toast.error('Enter an organization name.'); return; }
    setCreating(true);
    try {
      const created = await axios.post(`${API}/orgs`, {
        name: orgName.trim(),
        type: orgType,
        requested_plan: commercialSignup ? createTier : 'coach',
      });

      setCreateOpen(false);
      setOrgName('');
      setLoading(true);
      await load();
      if (commercialSignup) {
        // Creation alone does not grant plan access. Any Checkout failure
        // leaves a visible pending organization with billing recovery.
        try {
          const checkout = await axios.post(`${API}/subscriptions/checkout`, {
            owner_type: 'organization',
            organization_id: created.data.id,
            plan_key: createTier,
          });
          if (checkout.data.url) {
            window.location.assign(checkout.data.url);
            return;
          }
          throw new Error('Stripe did not return a Checkout URL');
        } catch (billingError) {
          toast.error(billingError.response?.data?.detail ||
            'Organization created; payment is still required. Open Billing to retry.');
        }
      } else {
        toast.success('Beta organization created with server-managed access.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900">
        <HamburgerMenu />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        </div>
      </div>
    );
  }

  const current = memberships[selectedIdx];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900" data-testid="teams-hub-page">
      <HamburgerMenu />
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {memberships.length === 0 ? (
          <div>
            {/* Landing */}
            <div className="text-center mb-10 mt-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-amber-900 to-amber-950 border-2 border-amber-700 mb-4 shadow-lg shadow-amber-900/40">
                <Users className="w-8 h-8 text-amber-400" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-wider">ANVIL &amp; ASH FOR TEAMS</h1>
              <p className="text-stone-400 mt-2 max-w-xl mx-auto">
                Coach smarter. Schools, gyms, and trainers get a live view of every athlete's training and nutrition — athletes train free.
              </p>
            </div>

            {/* Join with code */}
            <Card className="glass border-stone-800 p-6 mb-10 max-w-xl mx-auto">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="w-5 h-5 text-amber-400" />
                <h2 className="text-lg font-bold text-white tracking-wider">HAVE AN INVITE CODE?</h2>
              </div>
              <p className="text-xs text-stone-400 mb-4">If a coach or school invited you, enter your code to join their organization. It's completely free for athletes.</p>
              <form onSubmit={handleJoin} className="flex gap-2">
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="e.g. 7F3A9C2B"
                  maxLength={12}
                  className="bg-stone-900 border-stone-700 text-white uppercase tracking-widest"
                  data-testid="join-code-input"
                />
                <Button
                  type="submit"
                  className="bg-amber-700 hover:bg-amber-600 text-white font-bold px-6"
                  data-testid="join-code-submit"
                >
                  Review Invite
                </Button>
              </form>
            </Card>

            {/* Pricing */}
            <h2 className="text-xl font-bold text-white tracking-wider text-center mb-1">FOR COACHES &amp; ORGANIZATIONS</h2>
            <p className="text-center text-amber-400 text-xs uppercase tracking-widest mb-6">{commercialSignup ? 'Monthly subscriptions — athletes join free' : 'Free during beta — billing coming soon'}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {PRICING.map((p) => (
                <Card key={p.tier} className={`glass p-5 flex flex-col ${p.popular ? 'border-amber-700 shadow-lg shadow-amber-900/30' : 'border-stone-800'}`} data-testid={`pricing-tier-${p.tier}`}>
                  {p.popular && <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Most Popular</div>}
                  <h3 className="text-white font-bold tracking-wide">{p.name}</h3>
                  <div className="mt-2 mb-1">
                    <span className="text-3xl font-bold text-white">{p.price}</span>
                    <span className="text-stone-400 text-sm">{p.per}</span>
                  </div>
                  <p className="text-xs text-stone-500 mb-4">{p.desc}</p>
                  <ul className="space-y-1.5 text-xs text-stone-300 mb-5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => {
                      if (p.tier !== 'enterprise') {
                        setCreateTier(p.tier);
                        setCreateOpen(true);
                      }
                    }}
                    disabled={!billingConfigReady || !billingConfig || p.tier === 'enterprise' ||
                      (!commercialSignup && p.tier !== 'coach') ||
                      (commercialSignup && !billingConfig?.plans?.[`organization:${p.tier}`])}
                    className={`w-full font-bold ${
                      p.tier !== 'enterprise' &&
                      (commercialSignup ? billingConfig?.plans?.[`organization:${p.tier}`] : p.tier === 'coach')
                        ? 'bg-amber-700 hover:bg-amber-600 text-white'
                        : 'bg-stone-900 text-stone-600 border border-stone-800 cursor-not-allowed'
                    }`}
                    data-testid={`pricing-select-${p.tier}`}
                  >
                    {p.tier === 'enterprise' ? 'Contact Sales' :
                      commercialSignup ? (billingConfig?.plans?.[`organization:${p.tier}`] ? 'Subscribe' : 'Unavailable') :
                      p.tier === 'coach' ? 'Start Beta' : 'Coming Soon'}
                  </Button>
                </Card>
              ))}
            </div>

            {/* Create form */}
            {createOpen && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !creating && setCreateOpen(false)}>
                <Card className="glass border-stone-800 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="create-org-modal">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-bold text-white tracking-wider">CREATE YOUR ORGANIZATION</h3>
                  </div>
                  <p className="text-xs text-stone-400 mb-4">
                    Plan: <span className="text-amber-400 font-bold">{PRICING.find((p) => p.tier === createTier)?.name}</span> — {commercialSignup ? 'monthly payment required before access' : 'free during beta, billing coming soon'}.
                  </p>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Organization Name</label>
                      <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Ironworks Gym" maxLength={100} className="bg-stone-900 border-stone-700 text-white" data-testid="create-org-name" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Type</label>
                      <Select value={orgType} onValueChange={setOrgType}>
                        <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="create-org-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700 text-white">
                          {ORG_TYPES.map((t) => <SelectItem key={t.value} value={t.value} className="focus:bg-stone-800 focus:text-white">{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating} className="flex-1 border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800">Cancel</Button>
                      <Button type="submit" disabled={creating} className="flex-1 bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="create-org-submit">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                      </Button>
                    </div>
                  </form>
                </Card>
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Org switcher */}
            {memberships.length > 1 && (
              <div className="mb-4 max-w-xs">
                <Select value={String(selectedIdx)} onValueChange={(v) => setSelectedIdx(Number(v))}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="org-switcher"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    {memberships.map((m, i) => (
                      <SelectItem key={m.organization.id} value={String(i)} className="focus:bg-stone-800 focus:text-white">{m.organization.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {STAFF_ROLES.includes(current.role) ? (
              <CoachDashboard org={current.organization} role={current.role} onChanged={load} />
            ) : (
              <AthleteOrgView org={current.organization} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AthleteOrgView = ({ org }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/coach/my-plans`)
      .then((res) => setPlans((res.data.plans || []).filter((p) => p.organization_id === org.id)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [org.id]);

  return (
    <div data-testid="athlete-org-view">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-amber-400 mb-1">My Organization</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wider">{org.name}</h1>
        <p className="text-stone-400 text-sm mt-2">
          Organization membership does not automatically share your sensitive
          fitness data. You control access for named professionals in Privacy
          &amp; Sharing.
        </p>

        <Button
          onClick={() => navigate(`/privacy-sharing/${org.id}`)}
          variant="outline"
          className="mt-4 border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800"
          data-testid="privacy-sharing-btn"
        >
          Privacy &amp; Sharing
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-5 h-5 text-amber-400" />
        <h2 className="text-lg font-bold text-white tracking-wider">COACH-ASSIGNED PLANS</h2>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      ) : plans.length === 0 ? (
        <Card className="glass border-stone-800 p-6 text-center">
          <p className="text-stone-400 text-sm">No plans assigned yet. When your coach assigns a workout plan, it will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => (
            <Card key={p.id} className="glass border-stone-800 p-4" data-testid={`assigned-plan-${p.id}`}>
              <button type="button" className="w-full text-left" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-amber-400" />
                      <span className="text-white font-bold tracking-wide">{p.name}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      {p.focus ? `${p.focus} · ` : ''}{(p.exercises || []).length} exercises · by {p.created_by_name || 'Coach'}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${expanded === p.id ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {expanded === p.id && (
                <div className="mt-3 border-t border-stone-800 pt-3 space-y-2">
                  {p.notes && <p className="text-xs text-stone-400 italic">{p.notes}</p>}
                  {(p.exercises || []).map((ex, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-stone-900/60 rounded px-3 py-2">
                      <div>
                        <span className="text-stone-200">{ex.exercise_name || ex.name}</span>
                        {ex.muscle_group && <span className="text-stone-500 text-xs ml-2">{ex.muscle_group}</span>}
                      </div>
                      <span className="text-stone-400 text-xs">
                        {(typeof ex.sets === 'number' ? ex.sets : Array.isArray(ex.sets) ? ex.sets.length : 3)} × {ex.reps || '8-12'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamsHub;
